import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, UntypedFormControl } from '@angular/forms';
import {
  MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent,
  MatLegacyAutocomplete as MatAutocomplete,
} from '@angular/material/legacy-autocomplete';
import { MatLegacyButton as MatButton } from '@angular/material/legacy-button';
import { MatLegacyChipInputEvent as MatChipInputEvent } from '@angular/material/legacy-chips';
import { filter as _filter, remove } from 'lodash-es';
import { BehaviorSubject, Subject } from 'rxjs';
import { provideValueAccessor } from 'studio-components/shared/value-accessor.utils';
import { Option } from '../../../select/public-api';

@Component({
  selector: 'studio-autocomplete',
  templateUrl: 'autocomplete.component.html',
  styleUrls: ['autocomplete.component.scss'],
  providers: [provideValueAccessor(() => AutocompleteComponent)],
})
export class AutocompleteComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;
  @ViewChild('inputClear') inputClear: MatButton;
  @ViewChild('inputSearch') inputSearch: MatButton;

  @Output() chipsChangeEmitter = new EventEmitter<Option[]>(); // emits the chips selected

  @Input() displayKey = 'value';
  @Input() uniqueId: string;
  @Input() placeholder = '';
  @Input() options: Option[] = [];
  @Input() multiSelect = false;
  @Input() selectedChips: Option[] = [];
  @Input() clearButtonTooltipText = 'Clear all';
  @Input() limitResults = 0;

  filteredOptions$ = new BehaviorSubject<Option[]>([]);
  options$ = new BehaviorSubject<Option[]>([]);
  formCtrl = new UntypedFormControl();
  isFocus = false;

  private destroyed$ = new Subject();

  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  ngOnInit() {
    this.setupOptions();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    const { options, selected } = changes;

    if (options) {
      this.setupOptions();
      this.updateSelectedChips(selected?.currentValue);
    }
  }

  constructor() {
    this.setupAutocomplete();
  }

  private setupOptions() {
    this.options$.next(this.options);
    this.filteredOptions$.next(this.options);
  }

  // when input changes (user is typing), filter list based on that input
  private setupAutocomplete() {
    this.formCtrl.valueChanges.subscribe((val) => {
      const newActiveOptions = this.filterOptionsList(val);
      this.filteredOptions$.next(newActiveOptions);
    });
  }

  private filterOptionsList(filterValue?: any) {
    return filterValue
      ? _filter(this.options$.getValue(), (chip) =>
          chip.text.toLowerCase().startsWith(`${filterValue}`?.toLowerCase()),
        )
      : this.options$.getValue().slice();
  }

  private getOptionFromId(id: string) {
    return this.options$.getValue().find((opt) => opt[this.displayKey] === id);
  }

  private updateSelectedChips(id: string) {
    const option = this.getOptionFromId(id);
    if (!option || id === undefined) return;

    if (this.selectedChips.includes(option)) {
      // unselect chip if already selected
      this.removeChip(option[this.displayKey]);
    } else if (this.multiSelect) {
      this.selectedChips.push(option);
    } else {
      this.selectedChips = [option];
    }
    return this.chipsChange();
  }

  chipsChange() {
    this.chipsChangeEmitter.emit(this.selectedChips);

    if (this.onChange) {
      const selectedIds = this.selectedChips.map((selected) => selected[this.displayKey]);
      this.onChange(selectedIds);
      this.onTouched();
    }
  }

  isOptionChecked(id: string) {
    return this.selectedChips.findIndex((chip) => id === chip[this.displayKey]) >= 0;
  }

  add(event: MatChipInputEvent) {
    const { input, value } = event;
    if ((value || '').trim()) {
      const isOptionSelected = this.matAutocomplete.options.some((option) => option.selected);
      if (!isOptionSelected) {
        this.updateSelectedChips(value.trim());
      }
    }
    // Reset the input value
    if (input) {
      input.value = '';
    }
  }

  clearInput(all = false) {
    this.input.nativeElement.value = '';
    this.formCtrl.setValue(null);

    if (all) {
      this.removeChip();
    }
  }

  removeChip(id?: string) {
    this.onFocus();
    if (id) {
      this.selectedChips = remove(this.selectedChips, (option) => option[this.displayKey] !== id);
    } else {
      // remove all selected chips
      this.selectedChips = [];
    }

    this.chipsChange();
  }

  selected(event: MatAutocompleteSelectedEvent) {
    event.option.deselect();
    this.updateSelectedChips(event.option.value);
    this.clearInput();
  }

  onFocus(): void {
    this.input.nativeElement.focus();
    this.isFocus = true;
  }

  addOnBlur(_: FocusEvent): void {
    this.isFocus = false;
    this.onTouched();
  }

  /**
   * REACTIVE FORMS INTEGRATION
   */
  writeValue(ids: string[]) {
    this.selectedChips = this.options$.value.filter((option) =>
      ids?.includes(option[this.displayKey]),
    );
  }

  onChange = (_: any) => {};
  onTouched = () => {};

  registerOnChange(fn: (value: any) => void) {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }
}
